/**
 * SoleadLab 2.0 — Batch Delete for area-selected indications
 *
 * The lasso / Shift+box selection mirrors indication ids into
 * mapStore.selectedFeatureIds — but MapboxDraw does not consider lasso'd
 * features "selected", so its native Backspace/trash never fires and batch
 * selections could be dragged but not deleted (owner request 2026-09-02).
 *
 * Delete / Backspace with 2+ selected indications opens ONE confirm dialog.
 * On server-backed sites the deletion rides the operations log as ONE
 * `batch_delete` operation (per-item CAS, full prior-row capture) — it shows
 * in the History panel and Undo re-inserts every marker, same contract as
 * batch rotate/status. Blob-only projects fall back to per-row local
 * removal (not undoable, same as their single deletes).
 *
 * Registered in CAPTURE phase with stopPropagation so MapboxDraw's own
 * un-confirmed keybinding trash can never race this flow for multi
 * selections. Single-feature deletion stays with Draw / the popup's Delete
 * button, unchanged.
 */

import { useEffect } from 'react';
import { useMapStore, useProjectStore, useUIStore } from '../../stores';
import { useIsReadOnlyViewer } from '../../hooks/useShareViewer';
import { batchApplyIndications, undoOperation, type BatchApplyItem } from '../../services/operationsApi';
import { fetchEntityIndications } from '../../services/indicationApi';
import type { Indication } from '../../stores/projectStore';

const BATCH_CHUNK = 200; // mirrors IndicationsTicket's chunking (server seq contract)

function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el) return false;
  return (
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.isContentEditable === true
  );
}

/** Full refetch → replace: the recovery path after conflicts/failures and
 *  after undo (the undo response carries versions, not content). */
async function resyncFromServer(siteId: string) {
  const { indications: fresh } = await fetchEntityIndications(siteId);
  useProjectStore.getState().replaceIndications(fresh);
}

async function runServerBatchDelete(siteId: string, targets: Indication[]) {
  const addNotification = useUIStore.getState().addNotification;
  const operationId = crypto.randomUUID();
  const items: BatchApplyItem[] = targets.map((ind) => ({
    id: ind.id,
    version: ind._version ?? 1,
  }));

  // Optimistic: one local write; draw canvas mirrors the store on its own
  // sync pass, but delete eagerly so markers vanish with the confirm.
  const draw = useMapStore.getState().draw;
  useProjectStore.getState().removeIndicationsLocal(items.map((i) => i.id));
  for (const it of items) {
    try {
      draw?.delete(it.id);
    } catch {
      /* feature may not be on the draw canvas */
    }
  }
  useMapStore.getState().setSelectedFeatureIds([]);

  let applied = 0;
  let skipped = 0;
  let failedChunks = 0;
  for (let i = 0; i < items.length; i += BATCH_CHUNK) {
    try {
      const res = await batchApplyIndications(
        siteId,
        operationId,
        'batch_delete',
        `Deleted ${items.length} indications`,
        items.slice(i, i + BATCH_CHUNK),
      );
      applied += res.applied;
      skipped += res.staleConflicts.length + res.missing.length;
    } catch {
      failedChunks++;
    }
  }

  if (skipped > 0 || failedChunks > 0) {
    // Some rows were NOT deleted server-side (changed elsewhere / chunk
    // failure) — resync so they reappear instead of being silently gone
    // locally while alive on the server.
    try {
      await resyncFromServer(siteId);
    } catch {
      /* next full load reconciles */
    }
  }

  const parts: string[] = [];
  if (skipped) parts.push(`${skipped} kept (changed elsewhere)`);
  if (failedChunks) parts.push(`${failedChunks} chunk(s) failed and were restored`);
  addNotification({
    type: failedChunks || skipped ? 'warning' : 'success',
    title: `Deleted ${applied} indication${applied === 1 ? '' : 's'}`,
    message: parts.length ? parts.join(', ') : undefined,
    duration: 30000,
    action:
      applied > 0
        ? {
            label: 'Undo',
            onClick: () => {
              void (async () => {
                try {
                  const res = await undoOperation(siteId, operationId);
                  await resyncFromServer(siteId);
                  addNotification({
                    type: res.conflicts.length ? 'warning' : 'success',
                    title: `Undo: ${res.restored} indication${res.restored === 1 ? '' : 's'} restored`,
                  });
                } catch (err) {
                  addNotification({
                    type: 'error',
                    title: 'Undo failed',
                    message: err instanceof Error ? err.message : 'Unknown error',
                  });
                }
              })();
            },
          }
        : undefined,
  });
}

export function useBatchDelete() {
  const isReadOnly = useIsReadOnlyViewer();

  useEffect(() => {
    if (isReadOnly) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (isTypingTarget(e.target)) return;

      const ids = useMapStore.getState().selectedFeatureIds;
      if (ids.length < 2) return;

      const indications = useProjectStore.getState().data.indications;
      const targets = indications.filter((i) => ids.includes(i.id));
      if (targets.length < 2) return;

      e.preventDefault();
      e.stopPropagation();

      const siteId = useProjectStore.getState().meta.siteId;
      useUIStore.getState().showConfirm({
        title: `Delete ${targets.length} indications?`,
        message: siteId
          ? `Delete the ${targets.length} selected indication markers? ` +
            'The deletion is recorded in History and can be undone.'
          : `Delete the ${targets.length} selected indication markers? ` +
            'This cannot be undone in a local project.',
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        variant: 'danger',
        onConfirm: () => {
          if (siteId) {
            void runServerBatchDelete(siteId, targets);
            return;
          }
          // Blob-only project: legacy per-row local removal.
          const draw = useMapStore.getState().draw;
          for (const t of targets) {
            useProjectStore.getState().removeIndication(t.id);
            try {
              draw?.delete(t.id);
            } catch {
              /* feature may not be on the draw canvas */
            }
          }
          useMapStore.getState().setSelectedFeatureIds([]);
        },
      });
    }

    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isReadOnly]);
}
